'use client'

import React, { useMemo, useCallback } from 'react'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { useAdmin } from '@/hooks/useAdmin'
import { cn } from '@/lib/utils'
import LanguagePicker from '@/components/ui/LanguagePicker'
import { getDashboardPath } from '@/utils/getDashboardPath'
import { isSelfLearnActive } from '@/utils/entitlements'
import {
  Menu, X, User, Settings, LogOut, ChevronDown, DollarSign,
  CreditCard, LayoutDashboard, Crown, MessageSquare, Building, Heart,
  GraduationCap, BookOpen, School, Church, Globe, Building2
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { PlanBadge } from '@/components/subscription/PlanBadge'
import { useFacilityBranding } from '@/contexts/SubdomainBrandingContext'
import BrandLogo from '@/components/common/BrandLogo'

const T = {
  sage: '#4a7c5e',
  sageDark: '#2d5a3d',
  sageLight: '#e8f2ec',
  sageMid: '#b8d9c4',
  cream: '#faf7f2',
  creamDark: '#f0ebe0',
  gold: '#c9903a',
  navy: '#1a2740',
  navyMid: '#243452',
  muted: '#8a94a6',
  white: '#ffffff',
  fontDisplay: "'Playfair Display', Georgia, serif",
  fontBody: "'Source Serif 4', Georgia, serif",
}

interface HeaderProps { className?: string }

const NAV_LINK_STYLE = (active: boolean): React.CSSProperties => ({
  padding: '7px 14px',
  borderRadius: '10px',
  textDecoration: 'none',
  fontFamily: T.fontBody,
  fontSize: '14px',
  fontWeight: 600,
  color: active ? T.white : 'rgba(255,255,255,0.6)',
  background: active ? 'rgba(74,124,94,0.35)' : 'transparent',
  border: active ? '1px solid rgba(184,217,196,0.25)' : '1px solid transparent',
  transition: 'all 0.15s',
})

const ENCLOSE_HOMESCHOOL_PARENTS = true

const Header: React.FC<HeaderProps> = ({ className }) => {
  const { user, userProfile, signOut } = useAuth()
  const { isAdmin } = useAdmin()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { t } = useTranslation()
  const activeTab = searchParams.get('tab') || 'features'
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [isSigningOut, setIsSigningOut] = React.useState(false)
  const menuRef = React.useRef<HTMLDivElement | null>(null)

  // White-label: adapt logo, name, and nav when on an org subdomain
  const facilityBranding = useFacilityBranding()
  const isBrandedSubdomain = facilityBranding?.is_branded ?? false

  // Hard gate: platform/whitelabel org — user stays entirely in their system
  // Declared early so branding derivation can use it
  const isPlatform = userProfile?.facility_type === 'platform'

  // For platform users on the main domain: derive branding from their facility profile
  // This gives org identity (name + logo) without a full site takeover
  const platformOrgBranding = !isBrandedSubdomain && isPlatform
    ? userProfile?.facility_branding
    : null

  // Unified: subdomain branding takes priority, else platform-user org branding
  const effectiveOrgName = isBrandedSubdomain
    ? facilityBranding?.display_name
    : platformOrgBranding?.display_name ?? null
  const effectiveOrgLogo = isBrandedSubdomain
    ? facilityBranding?.logo_url
    : platformOrgBranding?.logo_url ?? null
  const showOrgBranding = isBrandedSubdomain || (isPlatform && !!effectiveOrgName)

  const orgAccent = facilityBranding?.secondary_color ||
    platformOrgBranding?.secondary_color || T.sage

  const getStartedHref = useMemo(() => {
    if (location.pathname !== '/') return '/auth/signup'
    if (activeTab === 'schools') return '/education-signup?hub=school'
    if (activeTab === 'families') return '/education-signup?hub=family_edu'
    if (activeTab === 'facilities') return '/facility-signup'
    return '/auth/signup'
  }, [location.pathname, activeTab])

  const signInHref = '/auth/login'

  React.useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current?.contains(e.target as Node)) return
      setMenuOpen(false)
    }
    document.addEventListener('click', handler)
    return () => document.removeEventListener('click', handler)
  }, [])

  React.useEffect(() => {
    if (!mobileOpen) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement
      if (!target.closest('.sage-mobile-menu') && !target.closest('[data-mobile-btn]')) {
        setMobileOpen(false)
      }
    }
    const tid = setTimeout(() => document.addEventListener('click', handler), 100)
    return () => { clearTimeout(tid); document.removeEventListener('click', handler) }
  }, [mobileOpen])

  const isPremium = userProfile?.subscription_status === 'premium'
  const isActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(path + '/')

  const handleSignOut = useCallback(async () => {
    if (isSigningOut) return
    setIsSigningOut(true)
    try { await signOut() } catch { }
    setMobileOpen(false)
    setMenuOpen(false)
    navigate('/', { replace: true })
    setIsSigningOut(false)
  }, [signOut, navigate, isSigningOut])

  // Dropdown menu items — role + facility-type aware
  const menuItems = useMemo(() => {
    const isHomeschool = userProfile?.facility_type === 'homeschool' || userProfile?.facility_type === 'family_edu'
    const isHomeschoolAdmin = isHomeschool && userProfile?.facility_role === 'admin'
    const isHomeschoolStudent = isHomeschool && (userProfile?.facility_role === 'member' || userProfile?.facility_role === 'student')
    const fType = userProfile?.facility_type
    const fRole = userProfile?.facility_role

    let items: Array<{ to: string; icon: any; label: string }> = []

    // ── HARD GATE: platform/whitelabel org users stay in their system ──────────
    if (fType === 'platform' && !isAdmin) {
      // Owner / admin
      if (fRole === 'admin') {
        return [
          { to: '/platform/setup', icon: School, label: 'Setup & Branding' },
          { to: '/education-admin', icon: School, label: 'Education Hub' },
          { to: '/profile', icon: User, label: t('navigation.profile', 'Profile') },
          { to: '/settings', icon: Settings, label: t('navigation.settings', 'Settings') },
        ]
      }
      // Teacher / HR / Staff
      if (fRole === 'teacher') {
        return [
          { to: '/education-admin', icon: School, label: 'Education Hub' },
          { to: '/profile', icon: User, label: t('navigation.profile', 'Profile') },
          { to: '/settings', icon: Settings, label: t('navigation.settings', 'Settings') },
        ]
      }
      // Student / Employee / Member
      if (fRole === 'student' || fRole === 'member' || fRole === 'resident') {
        return [
          { to: '/learn', icon: GraduationCap, label: 'My Learning' },
          { to: '/profile', icon: User, label: t('navigation.profile', 'Profile') },
          { to: '/settings', icon: Settings, label: t('navigation.settings', 'Settings') },
        ]
      }
      // Fallback platform user
      return [
        { to: '/learn', icon: GraduationCap, label: 'My Learning' },
        { to: '/profile', icon: User, label: t('navigation.profile', 'Profile') },
        { to: '/settings', icon: Settings, label: t('navigation.settings', 'Settings') },
      ]
    }

    if (isHomeschoolAdmin && ENCLOSE_HOMESCHOOL_PARENTS) {
      items = [
        { to: '/homeschool/parent', icon: Heart, label: t('layout.header.parentDashboard', 'Parent Dashboard') },
        ...(isSelfLearnActive(userProfile) ? [{ to: '/learn', icon: BookOpen, label: 'My Learning' }] : []),
        { to: '/homeschool/subscription', icon: CreditCard, label: t('navigation.subscription', 'Subscription') },
        { to: '/how-it-works', icon: BookOpen, label: t('navigation.howItWorks', 'How It Works') },
        { to: '/profile', icon: User, label: t('navigation.profile', 'Profile') },
        { to: '/settings', icon: Settings, label: t('navigation.settings', 'Settings') },
        { to: '/sponsor', icon: Heart, label: t('layout.header.sponsor', 'Sponsor a Child') },
      ]
    } else if (isHomeschoolStudent) {
      items = [
        { to: '/learn', icon: GraduationCap, label: t('layout.header.myLearning', 'My Learning') },
        { to: '/how-it-works', icon: BookOpen, label: t('navigation.howItWorks', 'How It Works') },
        { to: '/profile', icon: User, label: t('navigation.profile', 'Profile') },
        { to: '/settings', icon: Settings, label: t('navigation.settings', 'Settings') },
      ]
    } else {
      items = [
        { to: '/learn', icon: GraduationCap, label: t('layout.header.myLearning', 'My Learning') },
        { to: '/how-it-works', icon: BookOpen, label: t('navigation.howItWorks', 'How It Works') },
        { to: '/profile', icon: User, label: t('navigation.profile') },
        { to: '/settings', icon: Settings, label: t('navigation.settings') },
        { to: '/subscription', icon: CreditCard, label: t('navigation.subscription') },
        { to: '/sponsor', icon: Heart, label: t('layout.header.sponsor', 'Sponsor a Child') },
      ]
    }

    // Affiliate dashboard for ANY affiliate (pending or approved)
    if (userProfile?.is_affiliate) {
      items.push({ to: '/affiliates/dashboard', icon: DollarSign, label: t('layout.header.affiliateDashboard', 'Affiliate Dashboard') })
    } else {
      items.push({ to: '/affiliates', icon: DollarSign, label: t('layout.header.becomeAffiliate', 'Become an Affiliate') })
    }

    // Smart logic for university students or users with university mode enabled
    const isUniversityStudent = fRole === 'university_student' ||
      (userProfile?.preferences as any)?.university_mode === true

    if (isUniversityStudent) {
      items.push({ to: '/university/dashboard', icon: GraduationCap, label: t('layout.header.universityDashboard') })
    }

    if (isAdmin) {
      items.push(
        { to: '/admin', icon: Crown, label: t('layout.header.platformAdmin', 'Platform Admin') },
        { to: '/platform/setup', icon: Globe, label: 'Platform Setup & Branding' },
        { to: '/homeschool/parent', icon: Heart, label: t('layout.header.homeschoolParent', 'Homeschool Parent') },
        { to: '/church/admin', icon: Church, label: t('layout.header.churchAdmin', 'Church Admin') },
        { to: '/ngo/dashboard', icon: Building2, label: 'NGO Dashboard' },
        { to: '/university/ngo/dashboard', icon: GraduationCap, label: 'University NGO Dashboard' },
        { to: '/university/dashboard', icon: GraduationCap, label: t('layout.header.universityDashboard', 'University Dashboard') },
        { to: '/education-admin', icon: School, label: t('layout.header.educationAdmin', 'Education Admin') },
        { to: '/facility-admin', icon: Building, label: t('layout.header.facilityDashboard', 'Facility Dashboard') },
        { to: '/sponsor/dashboard', icon: Heart, label: 'Sponsor Dashboard' },
        { to: '/learn', icon: BookOpen, label: t('layout.header.studentLearning', 'Student Learning') },
        { to: '/church/learn', icon: MessageSquare, label: t('layout.header.churchLearning', 'Church Learning') },
      )
    } else if (fRole === 'admin' || fRole === 'teacher') {
      if (fType === 'church') {
        items.push({ to: '/church/admin', icon: Building, label: t('layout.header.churchAdmin') })
      } else if (fType === 'homeschool' || fType === 'family_edu') {
        items.push({ to: '/homeschool/parent', icon: Heart, label: t('layout.header.parentDashboard') })
        if (isSelfLearnActive(userProfile)) {
          items.push({ to: '/learn', icon: BookOpen, label: 'My Learning' })
        }
      } else if (fType === 'school' || fRole === 'teacher' || user?.user_metadata?.is_education_admin === true) {
        items.push({ to: '/education-admin', icon: Crown, label: t('layout.header.educationHub') })
      } else if (fType === 'family' || user?.user_metadata?.is_family_admin === true) {
        items.push({ to: '/family-admin', icon: Heart, label: t('layout.header.familyDashboard') })
      } else {
        items.push({ to: '/facility-admin', icon: Building, label: t('layout.header.facilityDashboard') })
      }
    } else if (
      fRole === 'member' ||
      fRole === 'student' ||
      fRole === 'resident' ||
      fRole === 'university_student'
    ) {
      if (fType === 'church') {
        items.push({ to: '/church/learn', icon: GraduationCap, label: t('layout.header.myBibleStudies') })
      } else if (fType === 'homeschool' || fType === 'family_edu' || fType === 'school' || fType === 'family') {
        items.push({ to: '/learn', icon: GraduationCap, label: t('layout.header.myLearning') })
      } else if (fType === 'university' || fRole === 'university_student') {
        items.push({ to: '/university/dashboard', icon: GraduationCap, label: t('layout.header.myUniversity') })
      } else if (fType === 'facility') {
        items.push({ to: '/learn', icon: LayoutDashboard, label: t('layout.header.myHome') })
      }
    }

    // De-duplicate items by label
    const uniqueItems: typeof items = []
    const seenLabels = new Set()
    for (const item of items) {
      if (!seenLabels.has(item.label)) {
        uniqueItems.push(item)
        seenLabels.add(item.label)
      }
    }

    return uniqueItems
  }, [t, isAdmin, isPlatform, userProfile?.facility_role, userProfile?.facility_type, userProfile?.preferences, user?.user_metadata])

  return (
    <header
      className={cn('sticky top-0 z-[100] w-full', className)}
      style={{
        background: `linear-gradient(135deg, ${T.navy} 0%, ${T.navyMid} 100%)`,
        borderBottom: '1px solid rgba(184,217,196,0.12)',
        boxShadow: '0 2px 20px rgba(26,39,64,0.3)',
      }}
    >
      {/* ── TOP BAR ── */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 16px' }}>
        <div className="flex items-center justify-between h-16 md:h-20">

          {/* Logo — org brand on subdomains, SWA on main domain */}
          <Link
            to={user ? (isAdmin ? '/admin' : getDashboardPath(userProfile, user?.user_metadata)) : (isBrandedSubdomain ? '/auth/login' : '/')}
            className="flex items-center gap-2.5 md:gap-3.5 no-underline shrink-0"
          >
            {showOrgBranding ? (
              <>
                {effectiveOrgLogo ? (
                  <BrandLogo
                    src={effectiveOrgLogo}
                    alt={effectiveOrgName || 'Organisation'}
                    style={{ height: '42px', maxWidth: '160px', objectFit: 'contain' }}
                  />
                ) : (
                  <div style={{
                    width: '38px', height: '38px', borderRadius: '10px',
                    background: `${orgAccent}22`, border: `1px solid ${orgAccent}55`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20,
                  }}>🎓</div>
                )}
                {effectiveOrgName && (
                  <span style={{ fontFamily: T.fontDisplay, fontWeight: 900, fontSize: 'clamp(16px, 3vw, 21px)', color: T.white, letterSpacing: '-0.02em', maxWidth: 220 }}>
                    {effectiveOrgName}
                  </span>
                )}
              </>
            ) : (
              <>
                <div className="w-10 h-10 md:w-14 md:h-14 flex items-center justify-center shrink-0">
                  <img src="/icons/blue-sage-logo.png" alt="Still Waters" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                </div>
                <span className="text-xl md:text-3xl whitespace-nowrap" style={{ fontFamily: T.fontDisplay, fontWeight: 900, color: T.white, letterSpacing: '-0.03em' }}>
                  Still Waters
                </span>
              </>
            )}
          </Link>

          {/* ── DESKTOP NAV (centre) ── */}
          {user ? (
            <nav className="hidden md:flex" style={{ alignItems: 'center', gap: '2px' }}>
              {/* ── HARD GATE: platform/whitelabel org ── */}
              {isPlatform && !isAdmin ? (
                <>
                  {(userProfile?.facility_role === 'admin') && (
                    <>
                      <Link to="/platform/setup" style={NAV_LINK_STYLE(isActive('/platform/setup'))}>Setup & Branding</Link>
                      <Link to="/education-admin" style={NAV_LINK_STYLE(isActive('/education-admin'))}>Education Hub</Link>
                    </>
                  )}
                  {(userProfile?.facility_role === 'teacher') && (
                    <Link to="/education-admin" style={NAV_LINK_STYLE(isActive('/education-admin'))}>Education Hub</Link>
                  )}
                  {(userProfile?.facility_role === 'student' || userProfile?.facility_role === 'member' || userProfile?.facility_role === 'resident') && (
                    <Link to="/learn" style={NAV_LINK_STYLE(isActive('/learn'))}>My Learning</Link>
                  )}
                </>
              ) : ['homeschool', 'family_edu'].includes(userProfile?.facility_type || '') && userProfile?.facility_role === 'admin' ? (
                <>
                  <Link to="/homeschool/parent" style={NAV_LINK_STYLE(isActive('/homeschool/parent'))}>
                    {t('layout.header.parentDashboard')}
                  </Link>
                  {isSelfLearnActive(userProfile) && (
                    <Link to="/learn" style={NAV_LINK_STYLE(isActive('/learn'))}>
                      My Learning
                    </Link>
                  )}
                  <Link to="/homeschool/subscription" style={NAV_LINK_STYLE(isActive('/homeschool/subscription'))}>
                    {t('navigation.subscription', 'Subscription')}
                  </Link>
                </>
              ) : ['homeschool', 'family_edu'].includes(userProfile?.facility_type || '') && (userProfile?.facility_role === 'student' || userProfile?.facility_role === 'member') ? (
                <Link to="/learn" style={NAV_LINK_STYLE(isActive('/learn'))}>
                  {t('layout.header.myLearning')}
                </Link>
              ) : (
                <>
                  {userProfile?.facility_type !== 'church' && (
                    <Link to={getDashboardPath(userProfile, user?.user_metadata)} style={NAV_LINK_STYLE(isActive('/dashboard') || isActive('/learn'))}>
                      {t('navigation.dashboard')}
                    </Link>
                  )}

                  {/* University student */}
                  {(userProfile?.facility_role === 'university_student' || (userProfile?.preferences as any)?.university_mode === true) && (
                    <Link to="/university/dashboard" style={NAV_LINK_STYLE(isActive('/university/dashboard'))}>
                      {t('layout.header.universityDashboard')}
                    </Link>
                  )}

                  {/* Church member */}
                  {userProfile?.facility_type === 'church' && userProfile?.facility_role === 'member' && (
                    <Link to="/church/learn" style={NAV_LINK_STYLE(isActive('/church/learn'))}>
                      {t('layout.header.myBibleStudies')}
                    </Link>
                  )}

                  {/* Church admin */}
                  {userProfile?.facility_type === 'church' && userProfile?.facility_role === 'admin' && (
                    <Link to="/church/admin" style={NAV_LINK_STYLE(isActive('/church/admin'))}>
                      {t('layout.header.churchAdmin')}
                    </Link>
                  )}

                  {/* Teacher / Education admin */}
                  {(userProfile?.facility_role === 'teacher' ||
                    (userProfile?.facility_type === 'school' && userProfile?.facility_role === 'admin') ||
                    user?.user_metadata?.is_education_admin === true) && (
                      <Link to="/education-admin" style={NAV_LINK_STYLE(isActive('/education-admin'))}>
                        {t('layout.header.educationHub')}
                      </Link>
                    )}

                  {/* My Learning for all non-church non-platform users */}
                  {userProfile?.facility_type !== 'church' && !isPlatform && (
                    <Link to="/learn" style={NAV_LINK_STYLE(isActive('/learn'))}>
                      {t('layout.header.myLearning', 'My Learning')}
                    </Link>
                  )}
                </>
              )}
            </nav>
          ) : isBrandedSubdomain ? (
            // Branded subdomain guest: sign-in CTA only — no SWA marketing links
            <nav className="hidden md:flex" style={{ alignItems: 'center', gap: '8px' }}>
              <span style={{ fontFamily: T.fontBody, fontSize: '13px', color: 'rgba(255,255,255,0.45)' }}>
                {facilityBranding?.display_name}
              </span>
            </nav>
          ) : (
            // Main domain guest nav
            <nav className="hidden md:flex" style={{ alignItems: 'center', gap: '2px' }}>
              <Link to="/homeschool" style={NAV_LINK_STYLE(isActive('/homeschool'))}>
                {t('layout.header.homeschooling')}
              </Link>
              <Link to="/schools" style={NAV_LINK_STYLE(isActive('/schools'))}>
                For Schools
              </Link>
              <Link to="/church" style={NAV_LINK_STYLE(isActive('/church'))}>
                {t('layout.header.churches')}
              </Link>
              <Link to="/?tab=facilities" style={NAV_LINK_STYLE(activeTab === 'facilities')}>
                {t('layout.header.facilities')}
              </Link>
              <Link to="/university" style={NAV_LINK_STYLE(false)}>
                {t('layout.header.university')}
              </Link>
              <Link to="/how-it-works" style={NAV_LINK_STYLE(isActive('/how-it-works'))}>
                {t('navigation.howItWorks', 'How It Works')}
              </Link>
              <Link to="/sponsor" style={NAV_LINK_STYLE(isActive('/sponsor'))} title="Sponsor a Child">
                💚
              </Link>
            </nav>
          )}

          {/* ── RIGHT SIDE ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <div className="hidden md:block"><LanguagePicker /></div>

            {user ? (
              <>
                <div className="hidden md:flex">
                  <PlanBadge userProfile={userProfile} />
                </div>

                {/* Desktop dropdown */}
                <div className="hidden md:flex" style={{ position: 'relative' }} ref={menuRef}>
                  <button
                    onClick={() => setMenuOpen(v => !v)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px',
                      padding: '6px 10px 6px 6px', borderRadius: '12px',
                      border: '1px solid rgba(184,217,196,0.2)',
                      background: menuOpen ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.08)',
                      cursor: 'pointer', transition: 'all 0.15s',
                    }}
                  >
                    <div style={{
                      width: '30px', height: '30px', borderRadius: '8px',
                      background: 'rgba(74,124,94,0.5)', border: '1px solid rgba(184,217,196,0.3)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <User size={15} style={{ color: T.sageMid }} />
                    </div>
                    <span style={{ fontFamily: T.fontBody, fontSize: '13px', fontWeight: 600, color: T.white }}>
                      {userProfile?.name?.split(' ')[0] || user?.email?.split('@')[0] || t('layout.header.account')}
                    </span>
                    <ChevronDown size={13} style={{ color: 'rgba(255,255,255,0.5)' }} />
                  </button>

                  {menuOpen && (
                    <div style={{
                      position: 'absolute', right: 0, top: 'calc(100% + 8px)',
                      width: '230px', background: T.white, borderRadius: '18px',
                      border: `1px solid ${T.creamDark}`,
                      boxShadow: '0 12px 40px rgba(26,39,64,0.2)',
                      overflow: 'hidden', zIndex: 50,
                    }}>
                      <div style={{ padding: '14px 18px', background: `linear-gradient(135deg, ${T.navy}, ${T.navyMid})` }}>
                        <div style={{ fontSize: '13px', fontWeight: 700, color: T.white }}>{user?.email}</div>
                        <div style={{ fontSize: '11px', color: T.sageMid, marginTop: '2px' }}>
                          {isPlatform || isBrandedSubdomain || userProfile?.facility_id
                            ? (userProfile?.facility_role === 'admin' ? '✦ Organization Admin' : userProfile?.facility_role === 'teacher' ? '✦ Course Manager' : '✦ Team Member')
                            : isPremium ? `✦ ${t('layout.header.premium')} ${t('layout.header.member')}` : t('layout.header.freeMember')}
                        </div>
                      </div>
                      {menuItems.map((item, idx) => {
                        const Icon = item.icon
                        return (
                          <Link
                            key={`${item.to}-${item.label}-${idx}`}
                            to={item.to}
                            onClick={() => setMenuOpen(false)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: '10px',
                              padding: '11px 18px', textDecoration: 'none',
                              color: T.navy, fontSize: '13px', fontFamily: T.fontBody, fontWeight: 500,
                            }}
                            onMouseEnter={e => (e.currentTarget.style.background = T.sageLight)}
                            onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                          >
                            <Icon size={15} style={{ color: T.sage }} /> {item.label}
                          </Link>
                        )
                      })}
                      <div style={{ borderTop: `1px solid ${T.creamDark}` }}>
                        <button
                          onClick={handleSignOut}
                          disabled={isSigningOut}
                          style={{
                            width: '100%', display: 'flex', alignItems: 'center', gap: '10px',
                            padding: '11px 18px', background: 'none', border: 'none',
                            cursor: 'pointer', color: '#dc2626', fontSize: '13px',
                            fontFamily: T.fontBody, textAlign: 'left',
                          }}
                          onMouseEnter={e => (e.currentTarget.style.background = '#fef2f2')}
                          onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                        >
                          <LogOut size={15} /> {isSigningOut ? t('layout.header.signingOut') : t('navigation.signOut')}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Mobile hamburger */}
                <button
                  className="flex md:hidden items-center justify-center"
                  onClick={() => setMobileOpen(v => !v)}
                  data-mobile-btn
                  style={{
                    width: '38px', height: '38px', borderRadius: '10px',
                    border: '1px solid rgba(184,217,196,0.2)',
                    background: 'rgba(255,255,255,0.08)', cursor: 'pointer',
                  }}
                >
                  {mobileOpen
                    ? <X size={18} style={{ color: T.white }} />
                    : <Menu size={18} style={{ color: T.white }} />}
                </button>
              </>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <Link
                  to={signInHref}
                  className="hidden md:block"
                  style={{
                    padding: '7px 16px', borderRadius: '10px', textDecoration: 'none',
                    fontFamily: T.fontBody, fontSize: '14px', fontWeight: 600,
                    color: 'rgba(255,255,255,0.75)',
                    border: '1px solid rgba(255,255,255,0.15)',
                    background: 'rgba(255,255,255,0.08)',
                  }}
                >
                  {t('navigation.signIn')}
                </Link>
                {/* Only show Get Started on the main SWA domain */}
                {!isBrandedSubdomain && (
                  <Link
                    to={getStartedHref}
                    className="hidden md:block"
                    style={{
                      padding: '7px 18px', borderRadius: '10px', textDecoration: 'none',
                      fontFamily: T.fontBody, fontSize: '14px', fontWeight: 700,
                      color: T.white, background: orgAccent,
                      boxShadow: `0 3px 12px ${orgAccent}66`,
                    }}
                  >
                    {t('navigation.getStarted')}
                  </Link>
                )}
                <button
                  className="flex md:hidden items-center justify-center"
                  onClick={() => setMobileOpen(v => !v)}
                  data-mobile-btn
                  style={{
                    width: '38px', height: '38px', borderRadius: '10px',
                    border: '1px solid rgba(184,217,196,0.2)',
                    background: 'rgba(255,255,255,0.08)', cursor: 'pointer',
                  }}
                >
                  {mobileOpen
                    ? <X size={18} style={{ color: T.white }} />
                    : <Menu size={18} style={{ color: T.white }} />}
                </button>
              </div>
            )}
          </div>
          {/* ── END RIGHT SIDE ── */}

        </div>
      </div>
      {/* ── END TOP BAR ── */}

      {/* ── MOBILE DRAWER ── */}
      {mobileOpen && (
        <div
          className="md:hidden sage-mobile-menu"
          style={{ background: T.navyMid, borderTop: '1px solid rgba(184,217,196,0.1)', padding: '16px' }}
        >
          {user ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {/* User identity card */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: '12px',
                padding: '12px 16px', background: 'rgba(255,255,255,0.06)',
                border: '1px solid rgba(184,217,196,0.15)', borderRadius: '14px', marginBottom: '8px',
              }}>
                <div style={{
                  width: '36px', height: '36px', borderRadius: '10px',
                  background: 'rgba(74,124,94,0.4)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <User size={18} style={{ color: T.sageMid }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, color: T.white, fontSize: '14px' }}>
                    {userProfile?.name || user?.email?.split('@')[0]}
                    <span className="ml-2 inline-block">
                      <PlanBadge userProfile={userProfile} />
                    </span>
                  </div>
                  <div style={{ fontSize: '12px', color: T.muted }}>{user?.email}</div>
                </div>
              </div>

              <div style={{ padding: '8px 4px' }}><LanguagePicker /></div>

              {menuItems.map((item, idx) => {
                const Icon = item.icon
                return (
                  <Link
                    key={`${item.to}-${item.label}-${idx}`}
                    to={item.to}
                    onClick={() => setMobileOpen(false)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '12px',
                      padding: '12px 16px', borderRadius: '12px',
                      textDecoration: 'none', color: 'rgba(255,255,255,0.8)',
                      fontSize: '15px', fontFamily: T.fontBody, fontWeight: 500,
                      background: isActive(item.to) ? 'rgba(74,124,94,0.25)' : 'transparent',
                    }}
                  >
                    <Icon size={17} style={{ color: T.sageMid }} /> {item.label}
                  </Link>
                )
              })}

              <button
                onClick={handleSignOut}
                disabled={isSigningOut}
                style={{
                  display: 'flex', alignItems: 'center', gap: '12px',
                  padding: '12px 16px', borderRadius: '12px',
                  border: 'none', background: 'none', cursor: 'pointer',
                  color: '#fca5a5', fontSize: '15px', fontFamily: T.fontBody, fontWeight: 500,
                  width: '100%', textAlign: 'left', marginTop: '8px',
                  borderTop: '1px solid rgba(255,255,255,0.08)',
                } as React.CSSProperties}
              >
                <LogOut size={17} style={{ color: '#fca5a5' }} />
                {isSigningOut ? t('layout.header.signingOut') : t('navigation.signOut')}
              </button>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', padding: '8px 0' }}>
              <div style={{ padding: '4px' }}><LanguagePicker /></div>
              {/* Only show SWA marketing links on the main domain */}
              {!isBrandedSubdomain && (
                <Link
                  to="/how-it-works"
                  onClick={() => setMobileOpen(false)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '12px 16px', borderRadius: '12px',
                    textDecoration: 'none', color: 'rgba(255,255,255,0.85)',
                    fontSize: '15px', fontFamily: T.fontBody, fontWeight: 600,
                    background: isActive('/how-it-works') ? 'rgba(74,124,94,0.25)' : 'transparent',
                  }}
                >
                  <BookOpen size={16} style={{ color: T.sageMid }} />
                  {t('navigation.howItWorks', 'How It Works')}
                </Link>
              )}
              <Link
                to={signInHref}
                onClick={() => setMobileOpen(false)}
                style={{
                  display: 'block', padding: '13px 16px', borderRadius: '12px',
                  border: `1px solid ${orgAccent}44`, background: 'rgba(255,255,255,0.08)',
                  textDecoration: 'none', color: T.white, fontFamily: T.fontBody,
                  fontWeight: 600, textAlign: 'center', fontSize: '15px',
                }}
              >
                {t('navigation.signIn')}
              </Link>
              {!isBrandedSubdomain && (
                <Link
                  to={getStartedHref}
                  onClick={() => setMobileOpen(false)}
                  style={{
                    display: 'block', padding: '13px 16px', borderRadius: '12px',
                    background: T.sage, textDecoration: 'none', color: T.white,
                    fontFamily: T.fontBody, fontWeight: 700, textAlign: 'center',
                    fontSize: '15px', boxShadow: '0 3px 12px rgba(74,124,94,0.35)',
                  }}
                >
                  {t('navigation.getStarted')}
                </Link>
              )}
            </div>
          )}
        </div>
      )}
      {/* ── END MOBILE DRAWER ── */}

    </header>
  )
}

export default Header